import React, { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";

/**
 * ROICalculator — "Are you paying more than Quill costs?"
 *
 * Lives on the V2 landing page between Features and Pricing. The whole point
 * is to anchor $12/mo against the real $400–1,200/yr a working crypto creator
 * already bleeds on CPAs, Bonsai-style invoicing, and Koinly-style tax tools.
 *
 * Math (defensible, conservative):
 *   • CPA fees for crypto creators ≈ $25/invoice (8949 + 1099 prep) + $200 base/yr,
 *     capped at $1,500/yr — based on average freelance creator CPA quotes.
 *   • Bonsai / HoneyBook   = $228/yr  (their lowest billed-annually tier).
 *   • Koinly / CoinTracker = $99/yr   (creator-volume "Newbie" plan).
 *   • Stripe rake avoided  = 2.9% × annual revenue  (creators using Quill bypass
 *     this entirely because payments settle direct-to-wallet).
 *   • Time saved on invoicing/follow-ups = 15 min × 0.5× hourly_rate per invoice.
 *
 * Quill Pro = $144/yr. Savings = max(0, total_alt − 144).
 *
 * No external deps. Pure CSS sliders. Mobile-first.
 */

const PRESETS = [
  { id: "cpa",    label: "CPA at tax time",      cost: (m, n) => Math.min(1500, 200 + n * 12 * 25) },
  { id: "bonsai", label: "Bonsai / HoneyBook",   cost: () => 228 },
  { id: "koinly", label: "Koinly / CoinTracker", cost: () => 99 },
  { id: "stripe", label: "Stripe / PayPal",      cost: (m) => m * 12 * 0.029 },
];

const fmt = (n) => "$" + Math.round(n).toLocaleString();

const useCountUp = (target, duration = 700) => {
  const [val, setVal] = useState(target);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const from = val;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setVal(from + (target - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return val;
};

const SliderRow = ({ label, value, min, max, step, onChange, format, testId }) => (
  <div data-testid={testId}>
    <div className="flex items-baseline justify-between mb-2">
      <div className="l2-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--l2-ink-2)" }}>
        {label}
      </div>
      <div className="l2-display text-[22px] md:text-[26px]" style={{ color: "var(--l2-ink)" }}>
        {format ? format(value) : value}
      </div>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="roi-slider w-full"
      data-testid={`${testId}-input`}
    />
  </div>
);

const ROICalculator = ({ onCTA }) => {
  const [monthlyRev, setMonthlyRev] = useState(3000);   // $/mo
  const [invoices, setInvoices] = useState(6);          // count/mo
  const [hourlyRate, setHourlyRate] = useState(60);     // $/hr (for time-cost)
  const [selected, setSelected] = useState({ cpa: true, bonsai: true, koinly: false, stripe: true });

  const breakdown = useMemo(() => {
    const rows = PRESETS
      .filter((p) => selected[p.id])
      .map((p) => ({ id: p.id, label: p.label, amount: p.cost(monthlyRev, invoices) }));
    // Time saved on invoicing chores: 15 min per invoice × 0.5× hourly rate × 12 mo
    const timeSaved = invoices * 12 * (15 / 60) * hourlyRate * 0.5;
    rows.push({ id: "time", label: "Time saved (≈15min/invoice)", amount: timeSaved });
    const total = rows.reduce((a, r) => a + r.amount, 0);
    const savings = Math.max(0, total - 144);
    return { rows, total, savings };
  }, [monthlyRev, invoices, hourlyRate, selected]);

  const animatedSavings = useCountUp(breakdown.savings);

  return (
    <section
      id="roi"
      className="relative max-w-7xl mx-auto px-5 md:px-10 py-12 md:py-20"
      data-testid="roi-calculator-section"
    >
      <div
        className="l2-mono text-[11px] uppercase tracking-[0.18em] mb-2"
        style={{ color: "var(--l2-ink-2)" }}
      >
        ROI Calculator
      </div>
      <h2
        className="l2-display"
        style={{ fontSize: "clamp(36px, 6vw, 72px)", color: "var(--l2-ink)" }}
      >
        Are you paying more<br />than Quill costs?
      </h2>
      <p
        className="mt-4 max-w-xl text-[15px] leading-relaxed"
        style={{ color: "var(--l2-ink-2)" }}
      >
        Most crypto-paid creators bleed $400–$1,200/yr on a CPA, a generic invoicing
        SaaS, a tax tool, and the 2.9% Stripe rake. Quill bundles all four for $144.
      </p>

      <div className="mt-8 md:mt-12 grid grid-cols-1 lg:grid-cols-5 gap-4 md:gap-6">
        {/* ── Inputs (left, 3 cols) ───────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
          className="lg:col-span-3 rounded-[24px] p-6 md:p-8"
          style={{
            background: "var(--l2-cream)",
            boxShadow: "0 20px 40px -16px rgba(31, 27, 23, 0.18)",
          }}
          data-testid="roi-inputs-card"
        >
          <div className="space-y-6">
            <SliderRow
              label="Monthly invoice revenue"
              value={monthlyRev}
              min={500}
              max={20000}
              step={500}
              onChange={setMonthlyRev}
              format={fmt}
              testId="roi-monthly-rev"
            />
            <SliderRow
              label="Invoices per month"
              value={invoices}
              min={1}
              max={30}
              step={1}
              onChange={setInvoices}
              testId="roi-invoice-count"
            />
            <SliderRow
              label="Your hourly rate"
              value={hourlyRate}
              min={20}
              max={250}
              step={5}
              onChange={setHourlyRate}
              format={(v) => "$" + v + "/hr"}
              testId="roi-hourly-rate"
            />
          </div>

          <div className="mt-7">
            <div
              className="l2-mono text-[10px] uppercase tracking-[0.18em] mb-3"
              style={{ color: "var(--l2-ink-2)" }}
            >
              What you&apos;re using today
            </div>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => {
                const active = selected[p.id];
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelected((s) => ({ ...s, [p.id]: !s[p.id] }))}
                    data-testid={`roi-toggle-${p.id}`}
                    aria-pressed={active}
                    className="l2-mono text-[12px] px-3.5 py-2 rounded-full transition-all cursor-pointer"
                    style={{
                      background: active ? "var(--l2-ink)" : "rgba(31, 27, 23, 0.08)",
                      color: active ? "var(--l2-cream)" : "var(--l2-ink)",
                      transform: active ? "translateY(-1px)" : "none",
                      boxShadow: active
                        ? "0 6px 14px -6px rgba(31, 27, 23, 0.45)"
                        : "none",
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* ── Result (right, 2 cols) ──────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 0.61, 0.36, 1] }}
          className="lg:col-span-2 rounded-[24px] p-6 md:p-8 flex flex-col relative overflow-hidden"
          style={{
            background: "var(--l2-ink)",
            color: "var(--l2-cream)",
            boxShadow: "0 30px 60px -20px rgba(31, 27, 23, 0.45)",
          }}
          data-testid="roi-result-card"
        >
          {/* Soft peach aurora behind the number */}
          <div
            aria-hidden
            className="absolute -top-20 -right-20 w-64 h-64 rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, var(--l2-peach) 0%, transparent 70%)",
              opacity: 0.55,
              filter: "blur(20px)",
            }}
          />
          <div
            className="l2-mono text-[10px] uppercase tracking-[0.22em] relative"
            style={{ color: "var(--l2-peach-2)" }}
          >
            You&apos;d save with Quill Pro
          </div>
          <div
            className="l2-display mt-2 relative"
            style={{
              fontSize: "clamp(56px, 8vw, 96px)",
              lineHeight: 1,
              color: "var(--l2-cream)",
            }}
            data-testid="roi-savings-number"
          >
            {fmt(animatedSavings)}
            <span className="l2-mono text-[14px] opacity-60 ml-2 align-middle">
              /yr
            </span>
          </div>

          <div className="mt-6 space-y-2 relative" data-testid="roi-breakdown">
            {breakdown.rows.map((r) => (
              <div
                key={r.id}
                className="flex items-baseline justify-between text-[13px]"
                style={{ opacity: 0.85 }}
              >
                <span>{r.label}</span>
                <span className="l2-mono">{fmt(r.amount)}/yr</span>
              </div>
            ))}
            <div
              className="mt-3 pt-3 flex items-baseline justify-between text-[13px]"
              style={{ borderTop: "1px solid rgba(255, 246, 229, 0.18)" }}
            >
              <span style={{ opacity: 0.7 }}>Total alt cost</span>
              <span className="l2-mono">{fmt(breakdown.total)}/yr</span>
            </div>
            <div className="flex items-baseline justify-between text-[13px]">
              <span style={{ opacity: 0.7 }}>Quill Pro</span>
              <span className="l2-mono">$144/yr</span>
            </div>
          </div>

          <button
            type="button"
            onClick={onCTA}
            data-testid="roi-cta-btn"
            className="l2-cta mt-7 w-full rounded-full py-3.5 cursor-pointer transition-transform hover:-translate-y-0.5 l2-display relative"
            style={{
              background: "var(--l2-peach-2)",
              color: "var(--l2-ink)",
              fontSize: "14px",
            }}
          >
            Start saving — it&apos;s free to try
          </button>
          <div
            className="mt-3 l2-mono text-[10px] text-center opacity-50 relative"
            style={{ color: "var(--l2-cream)" }}
          >
            No card. No KYC. Cancel anytime.
          </div>
        </motion.div>
      </div>

      {/* ── Slider styles — scoped to this component */}
      <style>{`
        .roi-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 6px;
          background: linear-gradient(90deg, var(--l2-peach) 0%, var(--l2-mint) 100%);
          border-radius: 9999px;
          outline: none;
          cursor: pointer;
        }
        .roi-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: var(--l2-ink);
          border: 3px solid var(--l2-cream);
          box-shadow: 0 4px 10px -2px rgba(31, 27, 23, 0.45);
          cursor: grab;
          transition: transform 0.15s ease-out;
        }
        .roi-slider::-webkit-slider-thumb:active {
          transform: scale(1.15);
          cursor: grabbing;
        }
        .roi-slider::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: var(--l2-ink);
          border: 3px solid var(--l2-cream);
          box-shadow: 0 4px 10px -2px rgba(31, 27, 23, 0.45);
          cursor: grab;
        }
      `}</style>
    </section>
  );
};

export default ROICalculator;
